/**
 * O aviso de que a janela de 24 horas do WhatsApp fechou.
 *
 * A Meta só aceita texto livre por 24h a partir da ÚLTIMA mensagem do cliente.
 * Passado isso, só template aprovado -- e o envio é recusado.
 *
 * Sem este aviso, a falha é silenciosa e da pior espécie: o operador responde
 * de manhã uma mensagem da noite, vê a PRÓPRIA RESPOSTA aparecer no chat, e o
 * cliente nunca recebe. Nada na tela diz que não chegou.
 *
 * A API já recusa o envio fora da janela (`Room::podeResponderLivre()`), e isso
 * é a rede embaixo. Mas rede embaixo não conserta o problema do operador: ele
 * precisa saber ANTES de digitar, para escolher outro caminho -- ligar, mandar
 * e-mail, ou pedir que o cliente reabra a conversa.
 *
 * ## Por que a decisão vem do servidor
 *
 * O campo `pode_responder_livre` é calculado na API e vai junto da sala. A
 * alternativa seria o front recalcular a partir de `whatsapp_last_inbound_at`,
 * e aí a regra das 24h viveria em DOIS lugares: quando a Meta mudar o prazo,
 * ou quando alguém quiser diferenciar por tenant, a tela continuaria dizendo
 * "pode responder" enquanto o servidor recusa.
 */

/** A sala é de WhatsApp? O chat do site não tem janela nenhuma. */
export const ehSalaDeWhatsapp = (room) => {
    // O telefone DO CLIENTE e o criterio. Nao `whatsapp_instance_id`: essa
    // coluna e da camada whatsapp-web.js, removida em 31/08, e o fluxo da
    // Meta nunca a preenche -- enquanto o painel decidia por ela, TODA
    // conversa de WhatsApp aparecia como chat do site.
    //
    // Por que o telefone SOZINHO basta, sem um `OR` com a instancia: o unico
    // fluxo vivo (Meta) sempre preenche o telefone, e o passado foi coberto
    // pela migracao 2026_08_28_090000, que copiou o `room_id` das 3 salas
    // historicas (que ERA o telefone) para `whatsapp_user_number`. Nao e que
    // sala com instancia e sem telefone seja impossivel -- `ClientService`
    // ainda consegue criar uma, num `POST /clients` com `whatsapp_number` e
    // sem `whatsapp_user_number` --, e sim que nenhum fluxo vivo faz isso. Se
    // um dia fizer, o `OR` se justifica; hoje ele so manteria de pe a ideia
    // de que a instancia morta ainda decide algo.
    if (room?.whatsapp_user_number) return true;

    // `GET /rooms/active-dashboard` nao devolve a sala inteira: e uma lista
    // montada a mao pela API, sem o telefone, com a decisao ja tomada -- pelo
    // MESMO `whatsapp_user_number`, em `RoomService::activeDashboard()`.
    //
    // Aceitar a decisao do servidor aqui e o que evita um SEGUNDO predicado
    // para a mesma pergunta, e esse e o motivo inteiro: foi a duplicacao da
    // regra que produziu o defeito que este codigo conserta. (Nao ha ganho de
    // privacidade nisso: o telefone ja chega nesta mesma tela pela fila, que
    // vem de `RoomService::waiting()` com os models inteiros e
    // `Room::$hidden = []`.)
    //
    // So o `true` explicito: um `0`, `''` ou `null` de outra serializacao
    // marcaria chat do site como WhatsApp, e ai o operador acreditaria numa
    // janela de 24h e num numero que nao existem.
    return room?.is_whatsapp === true;
};

/**
 * O aviso a mostrar, ou `null` quando não há o que avisar.
 *
 * `null` para sala do site e para janela aberta -- avisar em qualquer um dos
 * dois seria ruído que o operador aprende a ignorar, e aí ele ignora também
 * quando importa.
 */
export const avisoDaJanela = (room) => {
    if (!ehSalaDeWhatsapp(room)) return null;

    // Só `false` explícito fecha a janela.
    //
    // `undefined` é sala vinda de um payload antigo, de antes deste campo
    // existir -- e aí NÃO avisamos. É o inverso da escolha do servidor, e de
    // propósito: lá, na dúvida, não entrega (proteger o cliente); aqui, na
    // dúvida, não assusta o operador com um aviso que pode ser falso.
    if (room.pode_responder_livre !== false) return null;

    return 'A janela de 24 horas do WhatsApp fechou. O cliente precisa escrever de novo para você conseguir responder — mensagens enviadas agora não chegam nele.';
};
